/**
 * TypeScript Type Definitions for Kørselsbog
 *
 * These types define the data structures used throughout the application.
 * They match the API contracts with the Azure Functions backend.
 */

// User & Authentication
export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  emailConfirmed?: boolean;
  lastLoginAt?: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  userId: string;
  email: string;
  name: string;
  expiresAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

// Trip
export enum TripType {
  Business = 'Business',
  Personal = 'Personal',
  Commute = 'Commute',
}

export interface Trip {
  id: string;
  userId: string;
  startAddress: string;
  startStreet?: string;
  startHouseNumber?: string;
  startZip?: string;
  startCity?: string;
  startLatitude?: number;
  startLongitude?: number;
  endAddress: string;
  endStreet?: string;
  endHouseNumber?: string;
  endZip?: string;
  endCity?: string;
  endLatitude?: number;
  endLongitude?: number;
  distance: number; // in kilometers
  purpose: string;
  date: string; // ISO date string
  startTime?: string; // HH:MM optional start time
  type: TripType;
  /** Google Calendar event ID — set when the trip was imported from Google Calendar */
  googleEventId?: string;
  /**
   * Effective rate per km (DKK) applying the 20,000 km annual threshold split.
   * Null until POST /api/trips/recalculate/{year} has been run for this trip's year.
   * Blended rate when a single trip crosses the threshold boundary.
   */
  ratePerKm?: number;
  /**
   * Computed reimbursement amount (DKK) after applying the threshold-aware rate.
   * Null until POST /api/trips/recalculate/{year} has been run.
   */
  totalAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTripDTO {
  startAddress: string;
  startStreet?: string;
  startHouseNumber?: string;
  startZip?: string;
  startCity?: string;
  startLatitude?: number;
  startLongitude?: number;
  endAddress: string;
  endStreet?: string;
  endHouseNumber?: string;
  endZip?: string;
  endCity?: string;
  endLatitude?: number;
  endLongitude?: number;
  distance?: number; // Will be calculated if not provided
  purpose: string;
  date: string;
  startTime?: string; // HH:MM optional start time
  type: TripType;
  googleEventId?: string;
}

export interface UpdateTripDTO {
  startAddress?: string;
  startStreet?: string;
  startHouseNumber?: string;
  startZip?: string;
  startCity?: string;
  startLatitude?: number;
  startLongitude?: number;
  endAddress?: string;
  endStreet?: string;
  endHouseNumber?: string;
  endZip?: string;
  endCity?: string;
  endLatitude?: number;
  endLongitude?: number;
  distance?: number;
  purpose?: string;
  date?: string;
  startTime?: string;
  type?: TripType;
  googleEventId?: string;
}

export interface TripFilters {
  startDate?: string;
  endDate?: string;
  type?: TripType;
  searchTerm?: string;
}

// Report
export interface MileageReport {
  id: string;
  userId: string;
  fiscalYearId: string;
  fiscalMonthId?: string;
  reportType: 'Month' | 'Year';
  totalKm: number;
  totalKmUnder20000: number;
  totalKmOver20000: number;
  totalAmount: number;
  monthNumber?: number;
  year?: number;
  pdfUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  fiscalYear?: FiscalYear;
  fiscalMonth?: FiscalMonth;
}

export interface ReportTrip {
  id?: string;
  reportId?: string;
  date: string; // yyyy-MM-dd
  startLocation: string;
  endLocation: string;
  distanceKm: number;
  purpose: string;
  isBusiness: boolean;
  googleEventId?: string;
}

export interface SaveReportDTO {
  fiscalYearId: string;
  fiscalMonthId?: string;
  reportType: 'Month' | 'Year';
  totalKm: number;
  totalKmUnder20000: number;
  totalKmOver20000: number;
  totalAmount: number;
  monthNumber?: number;
  year?: number;
  pdfUrl?: string | null;
  trips: ReportTrip[];
}

/** Minimal request sent to POST /api/reports/generate — backend fetches all data from DB */
export interface GenerateReportDTO {
  reportType: 'Month' | 'Year';
  year: number;
  /** Month number 1–12. Required when reportType == 'Month'. */
  month?: number;
  /** Optional explicit fiscal year ID. Auto-resolved by year when omitted. */
  fiscalYearId?: string;
}

// User Settings
export interface UserSettings {
  id: string;
  userId: string;
  name: string;
  homeAddress: string;
  licensePlate: string;
  cvr: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertSettingsDTO {
  name: string;
  homeAddress: string;
  licensePlate: string;
  cvr: string;
}

// Fiscal Years
export interface FiscalYear {
  id: string;
  userId: string;
  year: number;
  rateUnder20000: number;
  rateOver20000: number;
  /** Day-of-month the period ends on. 31 = standard last day of month. */
  endDay: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFiscalYearDTO {
  year: number;
  rateUnder20000: number;
  rateOver20000: number;
  /** Day-of-month each period ends on (1–28 for offset, 31 for standard). Default: 31 */
  endDay?: number;
}

export interface UpdateFiscalYearDTO {
  rateUnder20000: number;
  rateOver20000: number;
}

// Fiscal Months
export interface FiscalMonth {
  id: string;
  fiscalYearId: string;
  monthNumber: number;
  monthName: string;
  startDate: string; // DateOnly: YYYY-MM-DD
  endDate: string; // DateOnly: YYYY-MM-DD
  updatedAt: string;
}

export interface UpdateFiscalMonthDTO {
  startDate: string;
  endDate: string;
}

// Calendar
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  location?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

// Saved Addresses
export interface SavedAddress {
  id: string;
  userId: string;
  label: string;
  address: string;
  isDefault: boolean;
  defaultPurpose: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSavedAddressDTO {
  label: string;
  address: string;
  defaultPurpose?: string | null;
}

export interface UpdateAddressPurposeDTO {
  purpose: string | null;
}

// Saved Purposes
export interface SavedPurpose {
  id: string;
  userId: string;
  label: string;
  sortOrder: number;
  createdAt: string;
}

export interface CreateSavedPurposeDTO {
  label: string;
}
